import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { chatService } from '../services/chat';
import { Message, Conversation } from '../types';

const Chat: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | undefined>(
    conversationId ? parseInt(conversationId) : undefined
  );
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (currentConversationId) {
      loadConversation(currentConversationId);
    }
  }, [currentConversationId]);

  const loadConversations = async () => {
    try {
      const convs = await chatService.getConversations();
      setConversations(convs);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadConversation = async (id: number) => {
    try {
      const conv = await chatService.getConversation(id);
      setMessages(conv.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    setInput('');
    setLoading(true);

    try {
      const response = await chatService.sendMessage({
        message: input,
        conversation_id: currentConversationId
      });

      // If new conversation, update ID
      if (!currentConversationId) {
        setCurrentConversationId(response.conversation_id);
        await loadConversations();
      }

      // Reload conversation to get updated messages
      await loadConversation(response.conversation_id);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExtractVocabulary = async () => {
    if (!currentConversationId) {
      alert('Start a conversation first!');
      return;
    }

    if (messages.length < 4) {
      alert('Have a longer conversation first (at least 2 exchanges) to extract meaningful vocabulary!');
      return;
    }

    setExtracting(true);
    try {
      const cards = await chatService.extractVocabulary(currentConversationId);
      
      if (cards.length === 0) {
        alert('No new vocabulary found in this conversation. Try discussing more topics!');
      } else {
        alert(`🎉 Success! Added ${cards.length} new word${cards.length > 1 ? 's' : ''} to your vocabulary!\n\nGo to SRS module to study them.`);
      }
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error extracting vocabulary');
    } finally {
      setExtracting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewConversation = () => {
    setCurrentConversationId(undefined);
    setMessages([]);
  };

  const handleDeleteConversation = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      try {
        await chatService.deleteConversation(id);
        await loadConversations();
        if (currentConversationId === id) {
          startNewConversation();
        }
      } catch (error) {
        console.error('Error deleting conversation:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="text-indigo-600 hover:text-indigo-800"
            >
              ☰
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-indigo-600 hover:text-indigo-800"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-indigo-600">Conversation Practice</h1>
          </div>
          <div className="flex items-center gap-4">
            {currentConversationId && messages.length >= 4 && (
              <button
                onClick={handleExtractVocabulary}
                disabled={extracting}
                className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 disabled:bg-gray-400 transition flex items-center gap-2"
                title="Extract vocabulary from this conversation"
              >
                {extracting ? '⏳ Extracting...' : '🎴 Extract Vocabulary'}
              </button>
            )}
            <span className="text-gray-700">Points: {user?.total_points}</span>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        {showSidebar && (
          <div className="w-64 bg-white shadow-lg p-4 overflow-y-auto">
            <button
              onClick={startNewConversation}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 mb-4"
            >
              + New Chat
            </button>

            <h3 className="font-bold mb-2">Previous Conversations</h3>
            <div className="space-y-2">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`p-2 rounded cursor-pointer hover:bg-gray-100 ${
                    currentConversationId === conv.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                  }`}
                >
                  <div
                    onClick={() => setCurrentConversationId(conv.id)}
                    className="flex-1"
                  >
                    <p className="text-sm truncate">{conv.title}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(conv.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConversation(conv.id);
                    }}
                    className="text-red-500 text-xs hover:text-red-700 mt-1"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Area */}
        <div className="flex-1 p-4 flex flex-col">
          <div className="bg-white rounded-lg shadow-lg flex-1 flex flex-col overflow-hidden">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-gray-500 mt-10">
                  <p className="text-xl mb-2">👋 Welcome {user?.username}!</p>
                  <p>Start practicing English by sending a message below.</p>
                  <p className="text-sm mt-4 text-gray-400">Your level: {user?.current_level}</p>
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg max-w-md mx-auto">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>💡 Tips:</strong>
                    </p>
                    <ul className="text-sm text-gray-700 text-left space-y-1">
                      <li>• I'll help you with grammar and vocabulary as we chat</li>
                      <li>• Don't worry about making mistakes - that's how you learn!</li>
                      <li>• After chatting, click "Extract Vocabulary" to save useful words to SRS</li>
                    </ul>
                  </div>
                </div>
              )}

              {messages.map((msg) => (
                <div key={msg.id}>
                  <div
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] p-4 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                  
                  {/* Grammar correction */}
                  {msg.correction && (
                    <div className="flex justify-start mt-2">
                      <div className="max-w-[70%] bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">
                          {msg.correction}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="text-gray-600">Typing...</p>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message in English..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows={2}
                  disabled={loading}
                />
                <button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
